/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {gridDefaultPageSize} from '@ion/applications/src/utils/helpers';
import {normalizeEntity} from '@ion/core/src/entities/EntityUtils';
import {translate} from '@ion/core/src/locale/locale';
import {
  error,
  info
} from '@ion/core/src/logger/logger';
import {
  createAccount,
  createAccountContact,
  createAccountMessageEndpoint,
  createContact,
  createEnterpriseAccount,
  createServiceProfile as createSP,
  deleteAccount,
  deleteAccountMessageEndpoint,
  deleteServiceProfile,
  getAccount,
  getAccountMessageEndpoint,
  getApplications,
  loadAccounts,
  updateAccount,
  updateAccountMessageEndpoint,
  updateContact,
  updateServiceProfile
} from '@ion/core/src/services/Connect';
import {entityId, setRel} from '@ion/core/src/services/ConnectUtils';
import GroupProperties from '@ion/core/src/services/GroupProperties';
import {
  Account,
  AdminGroupsFilters,
  AuditChange,
  Entity,
  GenericObject,
  Link,
  Links,
  MessageEndpoint,
  ServiceProfile
} from '@ion/core/src/types/entities';
import {
  LoadAccountsParams,
  ServiceError
} from '@ion/core/src/types/services';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {ACCOUNT_EDIT_ERR_MSG, ACCOUNT_ERR_MSG_PATH} from '@ion/dls/src/utils/ComponentUtils';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import getQueryClient from '@ion/query/src/config/client';
import QueryKey, {createKey} from '@ion/query/src/config/QueryKeys';
import {retryUpdate} from '@ion/services/src/helpers/retry';
import {PageState} from '@/types/pages';
import {
  AdminAction,
  AsyncActionCreator,
  AsyncState,
  PageInitializeAction
} from '@/types/redux';
import {
  getAccountId,
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns,
  processErrorMessage
} from '../../../helpers';
import {
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../../prompts/constants';
import {importConfirm} from '../../../store/actions/importer';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';
/**
 * ACTIONS
 */
// name of the reducer
export const NAME                                  = 'adminGroups';

export const INITIALIZE_ADMIN_GROUPS               = 'INITIALIZE_ADMIN_GROUPS';

// grid column state
export const SET_GRID_COLUMNS                      = 'SET_ADMIN_GROUPS_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                   = 'UPDATE_ADMIN_GROUPS_GRID_COLUMNS';

// grid sub groups
export const SET_VIEW_SUB_GROUPS                   = 'SET_ADMIN_GROUPS_VIEW_SUB_GROUPS';

//grid rows per page
export const SET_GRID_PAGE_SIZE                    = 'SET_ADMIN_GROUPS_GRID_PAGE_SIZE';

// groups / accounts retrieval ...
export const GET_ADMIN_GROUPS_REQUEST              = 'GET_ADMIN_GROUPS_REQUEST';
export const GET_ADMIN_GROUPS_RESPONSE             = 'GET_ADMIN_GROUPS_RESPONSE';
export const GET_ADMIN_GROUPS_ERROR                = 'GET_ADMIN_GROUPS_ERROR';

// group / account add ...
export const ADD_ADMIN_GROUP                       = 'ADD_ADMIN_GROUP';
export const ADD_ADMIN_GROUP_REQUEST               = 'ADD_ADMIN_GROUP_REQUEST';
export const ADD_ADMIN_GROUP_RESPONSE              = 'ADD_ADMIN_GROUP_RESPONSE';
export const ADD_ADMIN_GROUP_ERROR                 = 'ADD_ADMIN_GROUP_ERROR';

// group / account view/edit ...
export const EDIT_ADMIN_GROUP                      = 'EDIT_ADMIN_GROUP';
export const EDIT_ADMIN_GROUP_REQUEST              = 'EDIT_ADMIN_GROUP_REQUEST';
export const EDIT_ADMIN_GROUP_RESPONSE             = 'EDIT_ADMIN_GROUP_RESPONSE';
export const EDIT_ADMIN_GROUP_ERROR                = 'EDIT_ADMIN_GROUP_ERROR';

// group / account  delete ...
export const DELETE_ADMIN_GROUP                    = 'DELETE_ADMIN_GROUP';
export const DELETE_ADMIN_GROUP_REQUEST            = 'DELETE_ADMIN_GROUP_REQUEST';
export const DELETE_ADMIN_GROUP_RESPONSE           = 'DELETE_ADMIN_GROUP_RESPONSE';
export const DELETE_ADMIN_GROUP_ERROR              = 'DELETE_ADMIN_GROUP_ERROR';

// view group / account audit log ...
export const AUDIT_ADMIN_GROUP                     = 'AUDIT_ADMIN_GROUP';
export const AUDIT_ADMIN_GROUP_REQUEST             = 'AUDIT_ADMIN_GROUP_REQUEST';
export const AUDIT_ADMIN_GROUP_RESPONSE            = 'AUDIT_ADMIN_GROUP_RESPONSE';
export const AUDIT_ADMIN_GROUP_ERROR               = 'AUDIT_ADMIN_GROUP_ERROR';
export const AUDIT_ADMIN_GROUP_SHOW_CHANGES_DIALOG = 'AUDIT_ADMIN_GROUP_SHOW_CHANGES_DIALOG';

// group bulk import
export const IMPORT_ADMIN_GROUPS                   = 'IMPORT_ADMIN_GROUPS';

// Grid refresh
export const ADMIN_GROUPS_GRID_REFRESHED           = 'ADMIN_GROUPS_GRID_REFRESHED';

// search queries
export const UPDATE_ADMIN_GROUPS_SEARCH_QUERIES    = 'UPDATE_ADMIN_GROUPS_SEARCH_QUERIES';
export const RESET_ADMIN_GROUPS_SEARCH_QUERIES     = 'RESET_ADMIN_GROUPS_SEARCH_QUERIES';

// account SSO config ...
export const ADD_SSO_CONFIG                        = 'ADD_SSO_CONFIG';
export const EDIT_SSO_CONFIG                       = 'EDIT_SSO_CONFIG';

interface EditAdminGroupArgsTypes {
  account: Account | number;
  show: boolean;
  isViewMode: boolean
};

/**
 * Initialize Groups
 */
export const initializeAdminGroups = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_GROUPS};
};

// Updates the modular search queries for Admin Group
export const updateAdminGroupsSearchQueries = (searchParams: AdminGroupsFilters) => ({
  type : UPDATE_ADMIN_GROUPS_SEARCH_QUERIES, searchParams
});

// Resets the Peg Labels filters state
export const resetAdminGroupsSearchQueries = () => ({
  type : RESET_ADMIN_GROUPS_SEARCH_QUERIES
});

/**
 * getAdminGroups return all accounts
 */
export const getAdminGroups = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  includeSubGroups = false,
  includeGlobalSearch = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_GROUPS_REQUEST});
    const {
      adminGroups: {
        search
      }
    } = getState();
    const parentAccountId = getAccountId(getState);
    const data: LoadAccountsParams = {
      parentAccountId,
      pageId,
      pageSize,
      includeSubGroups,
      includeGlobalSearch
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    data.filterOn = {
      ...data.filterOn,
      ...(!includeGlobalSearch && createModularSearchFilters(search))
    };
    try {
      const {accounts: items, totalCount} = await loadAccounts(data);
      dispatch({type: GET_ADMIN_GROUPS_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn?.({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_GROUPS_ERROR, error: err});
      return err;
    }
  };
};

/**
 * Given a group, assigns the group's parent account applications to the group.
 */
export const setGroupAssignedApplications = async(group: Account): Promise<Account> => {
  const applications = await getApplications(entityId(group.parent), true);
  group.applications = applications as Links;
  return group;
};

/**
 * Invoked when the Add Admin Group editor is to be displayed or removed.
 */
export const addAdminGroup = (show = true) => ({
  type : ADD_ADMIN_GROUP, show
});

/**
 * Invoked when a new group is to be created; consolidating the process of Group
 * creation into a single transaction. The process for new Group creation is as
 * outlined below.
 *
 * 1.) Retreives the applications assigned to the parent account of the group
 *     that is to be created.
 *
 * 2.) Creates the new Group (with the parent account's applications assigned
 *     to the Group).
 *
 * 3.) Notifies the MicroServices of the account changes.
 *
 * 4.) Dispatches an initialization refresh event, which informs the initialization
 *     state to reload the account hierarchy.
 *
 */
export const addGroup = (group: Account, isCtcAdmin: boolean, refreshInitialization: Function): AsyncActionCreator => {
  const entity = isCtcAdmin ? Entity.account : Entity.group;
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: ADD_ADMIN_GROUP_REQUEST});
    if (!isCtcAdmin) {
      group = await setGroupAssignedApplications(group);
    }
    try {
      let created;
      let errorMessage = '';
      if (isCtcAdmin && group?.enterprise) {
        const accountObj = {
          ...group,
          erpId        : group.externalRefID,
          parent       : group.parent?.href,
          subscription : group.subscription?.href
        };
        const {results, errors} = await createEnterpriseAccount(accountObj);
        created = results;
        errorMessage = errors?.length ? errors[0] : '';
      } else {
        created = await createAccount(group);
      }
      if (isCtcAdmin && group?.accountContacts) {
        createContacts(group, created?._self);
      }
      if (isCtcAdmin && group?.serviceProfile) {
        createServiceProfile(group.serviceProfile, created?._self);
      }
      if (isCtcAdmin && group?.messageEndpoint) {
        await createAccountMessageEndpoint(created, group?.messageEndpoint);
      }
      await dispatch(refreshInitialization());
      if (errorMessage) {
        dispatch(toggleUserPrompt({
          type                : 'success',
          partialSuccessState : `${translate('admin-groups:dialog.accountSuccess')} ${errorMessage}`,
          actionLabel         : translate('common:editors.delete.ok'),
          autoHide            : false
        }));
      } else {
        dispatch(toggleUserPrompt(successAddOptions(entity)));
      }
      dispatch({type: ADD_ADMIN_GROUP_RESPONSE, group: created});
    } catch (err) {
      const accountData           = safeJSONParse(err?.data);
      const errorMessageToDisplay = processErrorMessage(err, accountData);
      dispatch(toggleUserPrompt({
        type                : 'error',
        partialSuccessState : errorMessageToDisplay,
        actionLabel         : translate('common:editors.delete.ok'),
        autoHide            : false
      }));
      dispatch({type: ADD_ADMIN_GROUP_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the Edit Admin Group editor is to be displayed or removed.
 */
export const editAdminGroup = ({account, show = true, isViewMode}: EditAdminGroupArgsTypes) => {
  return async(dispatch: Function): Promise<any> => {
    try {
      const load  = typeof account === 'number';
      const group = await (load ? getAccount(account) : Promise.resolve(account));
      dispatch({type: EDIT_ADMIN_GROUP, group, show, isViewMode});
    } catch (err) {
      error(`Account loading is failed for account id: ${account}`);
    }
  };
};

/**
 * Invoked when an existing group is to be updated; consolidating the process of
 * Group updates into a single transaction. The process for editing a Group is
 * outlined below.
 *
 * 1.) Update the given group against CTC Services.
 *
 * 2.) Notifies the MicroServices of the account changes.
 *
 * 3.) Dispatches an initialization refresh event, which informs the initialization
 *     state to reload the account hierarchy.
 *
 * 4.) Remove the groups properties from the GroupProperties cache.
 *
 */
export const editGroup = (group: Account, isCtcAdmin: boolean, refreshInitialization: Function): Function => {
  const entity = isCtcAdmin ? Entity.account : Entity.group;
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_GROUP_REQUEST});
    try {
      const updated = await retryUpdate({
        newEntity    : group,
        updateEntity : updateAccount,
        getEntity    : getSingleGroup,
        onError      : groupUpdateError,
        dispatch
      });
      if (updated) {
        if (isCtcAdmin) {
          await handleMessageEndpoint(group, dispatch);
          if (group?.accountContacts) {
            createContacts(group, updated?._self);
          }
          if (group?.serviceProfile) {
            createServiceProfile(group.serviceProfile, updated?._self, true);
          }
        }
        await dispatch(refreshInitialization());
        GroupProperties.removeFromCache(updated.id);
        dispatch({type: EDIT_ADMIN_GROUP_RESPONSE, group: updated});
        dispatch(toggleUserPrompt(successEditOptions(entity)));
      }
    } catch (err) {
      groupUpdateError(err, dispatch);
    }
  };
};

const handleMessageEndpoint = async(group: Account, dispatch) => {
  const messageEndpoint = group?.messageEndpoint;
  const accountId = String(group?.id);
  if (messageEndpoint) {
    try {
      await getAccountMessageEndpoint(accountId);
      await handleMessageEndpointExists(group, messageEndpoint, dispatch);
    } catch (err) {
      if (err?.is404) {
        await handleNoMessageEndpoint(group, messageEndpoint);
      } else {
        error(err);
      }
    }
  } else if (group?.doDeleteMsgEP) {
    await handleMessageEndpointRemoved(group);
  }
  getQueryClient().removeQueries(createKey(QueryKey.MESSAGE_ENDPOINT, accountId));
};

const handleMessageEndpointExists = async(group: Account, messageEndpoint: MessageEndpoint, dispatch) => {
  try {
    await updateAccountMessageEndpoint(group, normalizeEntity(messageEndpoint) as MessageEndpoint);
  } catch (err) {
    dispatch(toggleUserPrompt(errorEditOptions(Entity.messageEndpoint, err)));
  }
};

const handleNoMessageEndpoint = async(group: Account, messageEndpoint: MessageEndpoint) => {
  try {
    await createAccountMessageEndpoint(group, messageEndpoint);
  } catch (err) {
    error('There was an error creating a new message endpoint for account: ', group.id);
  }
};

const handleMessageEndpointRemoved = async(group: Account) => {
  try {
    await deleteAccountMessageEndpoint(group);
  } catch (err) {
    error('There was no message endpoint to delete for account: ', group.id);
  }
};

/**
 * Invoked when an existing Group gets resource out of state, delegating to the fetch
 * latest group details successful response or error action accordingly.
 */
const getSingleGroup = async(groupID: number) => {
  try {
    const group = await getAccount(groupID);
    return {updated: group, error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

/**
 * Invoked when an existing Group gets updated or fetched, CTC API
 * and dispatching a successful or error action accordingly.
 */
const groupUpdateError = (err, dispatch: Function) => {
  const {errorText} = err || {};
  const isEntityFormatter = errorText?.includes(translate(ACCOUNT_ERR_MSG_PATH)) ||
  errorText?.includes(translate(ACCOUNT_EDIT_ERR_MSG)) || false;
  dispatch({type: EDIT_ADMIN_GROUP_ERROR, error: err});
  dispatch(toggleUserPrompt(errorEditOptions(Entity.account, err, isEntityFormatter)));
  error(err);
};

/**
 * Invoked when the Delete Admin Account Dialog is to be displayed or removed.
 */
export const deleteAdminGroup = (group: Account, show = true) => ({
  type : DELETE_ADMIN_GROUP, group, show
});

/**
 * Invoked when an existing group is to be deleted; consolidating the process of
 * Group delete into a single transaction. The process for deleting a Group is
 * outlined below.
 *
 * 1.) Delete the given group against CTC Services.
 *
 * 2.) Notifies the MicroServices of the account changes.
 *
 * 3.) Dispatches an initialization refresh event, which informs the initialization
 *     state to reload the account hierarchy.
 *
 */
export const deleteGroup = (group: Account, refreshInitialization: Function) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_GROUP_REQUEST});
    try {
      const deleted = await deleteAccount(group);
      try {
        await deleteAccountMessageEndpoint(deleted);
      } catch (err) {
        info('There was no message endpoint to delete for account with id: ', deleted.id);
      }
      await dispatch(refreshInitialization());
      GroupProperties.removeFromCache(deleted.id);
      dispatch({type: DELETE_ADMIN_GROUP_RESPONSE, group: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_GROUP_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the Audit Admin Group editor is to be displayed or removed.
 */
export const auditAdminGroup = (group: Account, show = true) => ({
  type : AUDIT_ADMIN_GROUP, group, show
});

export const showAuditChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_ADMIN_GROUP_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * Invoked when the Add Admin Group editor is to be displayed or removed.
 */
export const importAdminGroups = (show = true, result = null) => {
  return async(dispatch: Function) => {
    dispatch(importConfirm(Entity.group));
    dispatch({type: IMPORT_ADMIN_GROUPS, show, result});
  };
};

/**
 * Invoked whenever any update such as adding, updating, removing, or bulk importing
 * of the group in Admin-Group page.
 */
export const adminGroupGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_GROUPS_GRID_REFRESHED};
};

/**
 * Invoked when the Add SSO Config editor is to be displayed or removed.
 */
export const addSSOConfig = (group: Account, show = true) => ({
  type : ADD_SSO_CONFIG, group, show
});

/**
 * Invoked when the Edit SSO Config editor is to be displayed or removed.
 */
export const editSSOConfig = (group: Account, show = true) => ({
  type : EDIT_SSO_CONFIG, group, show
});

/**
 * Invoked whenever user click on View Sub Accounts toggle button
 */
export const setAdminGroupsViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setAdminGroupsGridPageSize = (pageSize: number = 20) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

/**
 * REDUCERS
 */
export interface AdminGroupsAction extends AdminAction {
  group?: Account;
  groups?: Account[];
  searchParams?: AdminGroupsFilters;
};

export interface AdminGroupsEditAction extends AdminAction {
  group?: Account;
  groups?: Account[];
  isViewMode: boolean;
};
export interface AdminGroupsActionState extends AsyncState {
  selected: Account | Account[] | null;
  result: Account | Account[] | null;
  isEditing: boolean;
  isViewMode: boolean;
};

export interface AdminGroupsAuditState extends AdminGroupsActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
}

interface AdminGroupImportState {
  isImporting: boolean;
  result: Account | Account[] | null;
};
export interface AdminGroupsState extends PageState {
  groups: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    pageSize: number
  },
  add: AdminGroupsActionState,
  edit: AdminGroupsActionState,
  destroy: AdminGroupsActionState,
  audit: AdminGroupsAuditState,
  bulkImport: AdminGroupImportState,
  search: AdminGroupsFilters,
  addSSO: AdminGroupsActionState,
  editSSO: AdminGroupsActionState
};

// defines the user preference key for the admin groups page ...
export const PREFERENCE = 'admin-groups';

// defines the initial state for the reducer ...
export const initialState: AdminGroupsState = {
  isInitialized : false,
  groups        : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    pageSize            : 20
  },
  add : {
    isEditing  : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    isViewMode : false
  },
  edit : {
    isEditing  : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    isViewMode : false
  },
  destroy : {
    isEditing  : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    isViewMode : false
  },
  audit : {
    isEditing         : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    isViewMode        : false,
    showChangesDialog : false,
    auditRecord       : null
  },
  bulkImport : {
    isImporting : false,
    result      : null
  },
  search : {
    id        : null,
    solomonId : null
  },
  addSSO : {
    isEditing  : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    isViewMode : false
  },
  editSSO : {
    isEditing  : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    isViewMode : false
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_GROUPS] : draft => {
    draft.isInitialized = true;
    const preference = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState = preference;
    draft.grid.preferedColumnState = preference;
  },

  [SET_GRID_COLUMNS] : ({grid, groups}: AdminGroupsState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : groups.loading,
      preference : PREFERENCE
    }, columnState);
  },

  [UPDATE_GRID_COLUMNS] : ({grid}: AdminGroupsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },

  [GET_ADMIN_GROUPS_REQUEST] : ({groups}: AdminGroupsState) => {
    groups.loading = true;
  },
  [GET_ADMIN_GROUPS_RESPONSE] : ({groups}: AdminGroupsState) => {
    groups.loading = false;
  },
  [GET_ADMIN_GROUPS_ERROR] : ({groups}: AdminGroupsState, {error}: AdminGroupsAction) => {
    groups.loading = false;
    groups.error = error;
  },
  [ADD_ADMIN_GROUP] : ({add}: AdminGroupsState, {show}: AdminGroupsAction) => {
    add.isEditing = show;
    add.result = null;
  },
  [ADD_ADMIN_GROUP_REQUEST] : ({add}: AdminGroupsState) => {
    add.loading = true;
    add.error = add.result = null;
  },
  [ADD_ADMIN_GROUP_RESPONSE] : ({add}: AdminGroupsState, {group}: AdminGroupsAction) => {
    add.loading = false;
    add.isEditing = false;
    add.result = group;
  },
  [ADD_ADMIN_GROUP_ERROR] : ({add}: AdminGroupsState, {error}: AdminGroupsAction) => {
    add.loading = false;
    add.error = error;
  },
  [EDIT_ADMIN_GROUP] : ({edit}: AdminGroupsState, {group, show, isViewMode}: AdminGroupsEditAction) => {
    edit.selected = show ? group : null;
    edit.isEditing = show;
    edit.isViewMode = isViewMode;
    edit.result = null;
  },
  [EDIT_ADMIN_GROUP_REQUEST] : ({edit}: AdminGroupsState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [EDIT_ADMIN_GROUP_RESPONSE] : ({edit}: AdminGroupsState, {group}: AdminGroupsAction) => {
    edit.loading = false;
    edit.isEditing = false;
    edit.result = group;
  },
  [EDIT_ADMIN_GROUP_ERROR] : ({edit}: AdminGroupsState, {error}: AdminGroupsAction) => {
    edit.loading = false;
    edit.error = error;
  },

  [DELETE_ADMIN_GROUP] : ({destroy}: AdminGroupsState, {group, show}: AdminGroupsAction) => {
    destroy.selected = show ? group : null;
    destroy.isEditing = show;
    destroy.result = null;
  },
  [DELETE_ADMIN_GROUP_REQUEST] : ({destroy}: AdminGroupsState) => {
    destroy.loading = true;
    destroy.error = destroy.result = null;
  },
  [DELETE_ADMIN_GROUP_RESPONSE] : ({destroy}: AdminGroupsState, {group}: AdminGroupsAction) => {
    destroy.loading = false;
    destroy.isEditing = false;
    destroy.result = group;
  },
  [DELETE_ADMIN_GROUP_ERROR] : ({destroy}: AdminGroupsState, {error}: AdminGroupsAction) => {
    destroy.loading = false;
    destroy.error = error;
    destroy.result = null;
  },
  [AUDIT_ADMIN_GROUP] : ({audit}: AdminGroupsState, {group, show}: AdminGroupsAction) => {
    audit.selected = show ? group : null;
    audit.isEditing = show;
    audit.result = null;
  },
  [AUDIT_ADMIN_GROUP_REQUEST] : ({audit}: AdminGroupsState) => {
    audit.loading = true;
    audit.error = audit.result = null;
  },
  [AUDIT_ADMIN_GROUP_RESPONSE] : ({audit}: AdminGroupsState, {result}: AdminGroupsAction) => {
    audit.loading = false;
    audit.isEditing = false;
    audit.result = result;
  },
  [AUDIT_ADMIN_GROUP_ERROR] : ({audit}: AdminGroupsState, {error}: AdminGroupsAction) => {
    audit.loading = false;
    audit.error = error;
  },
  [AUDIT_ADMIN_GROUP_SHOW_CHANGES_DIALOG] : ({audit}: AdminGroupsState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord = auditRecord;
  },
  [IMPORT_ADMIN_GROUPS] : ({bulkImport}: AdminGroupsState, {show, result}: AdminGroupsAction) => {
    bulkImport.isImporting = show;
    bulkImport.result = result;
  },
  [ADMIN_GROUPS_GRID_REFRESHED] : (
    {add, edit, audit, destroy, bulkImport}: AdminGroupsState
  ) => {
    add.result = null;
    edit.result = null;
    audit.result = null;
    destroy.result = null;
    bulkImport.result = null;
  },
  [UPDATE_ADMIN_GROUPS_SEARCH_QUERIES] : ({search}: AdminGroupsState, {searchParams}) => {
    search.id        = searchParams.id ?? null;
    search.name      = searchParams.name ?? null;
    search.status    = searchParams.status ?? null;
    search.solomonId = searchParams.solomonId ?? null;
  },
  [RESET_ADMIN_GROUPS_SEARCH_QUERIES] : ({search}: AdminGroupsState) => {
    search.id        = null;
    search.name      = null;
    search.status    = null;
    search.solomonId = null;
  },
  [SET_VIEW_SUB_GROUPS] : ({grid}: AdminGroupsState, {viewSubGroups}) => {
    grid.includeSubGroups = viewSubGroups;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: AdminGroupsState, {pageSize}) => {
    grid.pageSize = pageSize;
  },
  [ADD_SSO_CONFIG] : ({addSSO}: AdminGroupsState, {group, show}: AdminGroupsAction) => {
    addSSO.selected = show ? group : null;
    addSSO.isEditing = show;
  },
  [EDIT_SSO_CONFIG] : ({editSSO}: AdminGroupsState, {group, show}: AdminGroupsAction) => {
    editSSO.selected = show ? group : null;
    editSSO.isEditing = show;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminGroupsState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});

// Perform Contact add/update
async function createContacts(group: GenericObject, account: Link) {
  const {accountContacts, pointOfContacts} = group || {};
  let contact = {
    ...accountContacts,
    account   : setRel(account, 'account'),
    addresses : group?.addresses
  };
  if (pointOfContacts) {
    contact.id = entityId(pointOfContacts[0]);
    await updateContact(contact);
  } else {
    contact = await createContact(contact);
  }
  await createAccountContact(entityId(account), contact);
};

// Performs Service profile Create/Update and Delete operations
async function createServiceProfile(serviceProfile: ServiceProfile, account: Link, isEdit = false) {
  const accountServiceProfile = {
    account,
    name        : serviceProfile?.name,
    spVersion   : serviceProfile?.spVersion,
    description : serviceProfile?.description
  } as ServiceProfile;

  if (serviceProfile?.name === 'None') {
    if (isEdit && serviceProfile?.id) {
      await deleteServiceProfile(serviceProfile.id);
    }
    return;
  }

  if (isEdit && serviceProfile?.id) {
    accountServiceProfile.id = serviceProfile.id;
    accountServiceProfile.version = serviceProfile?.version;
    await updateServiceProfile(accountServiceProfile);
  } else {
    await createSP(accountServiceProfile);
  }
};
